import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  shareReplay,
  startWith,
  take,
  withLatestFrom
} from 'rxjs/operators';

import { PinnedItem } from '../../atlas_sdk/api';
import { BusinessNavConfigService } from '../../config.service';
import { BusinessNavDataService } from '../../data.service';
import { BusinessNavLocationService } from '../../modals/location-switcher-modal/location.service';
import { PinsService } from '../../pins.service';
import { ProductLogoBankService } from '../../product-logo-bank.service';
import { AtlasTranslateService } from '../../translate/translate.service';
import { ModalService } from '../modal/modal.service';
import { UserRequiredModalComponent } from '../../modals/user-required/user-required.modal.component';

@Component({
  selector: 'atlas-nav-item',
  templateUrl: './nav-item.component.html',
  styleUrls: ['./nav-item.component.scss']
})
export class NavItemComponent implements OnInit {
  @Input() url: string;
  @Input() path: string;
  @Input() icon: string;
  @Input() label: string;
  @Input() translationId: string;
  @Input() productId: string;
  @Input() serviceProviderId: string;
  @Input() logoUrl?: string;
  @Input() pinnable: boolean;
  @Input() navigationId: string;
  @Input() indentation: number;
  @Input() showIcon: boolean;
  @Input() chipContent: string;
  @Input() userRequired: boolean;
  @Input() ooProduct: boolean;
  @Input() productNavigation: boolean;
  isPinned$: Observable<boolean>;
  text$: Observable<string>;
  curServiceProviderId: Observable<string>;
  formattedUrl: Observable<string>;
  formattedPath$: Observable<string>;
  isActive$: Observable<boolean>;

  constructor(
    private router: Router,
    private apiService: BusinessNavDataService,
    private pinService: PinsService,
    private t: AtlasTranslateService,
    private configService: BusinessNavConfigService,
    private el: ElementRef,
    private locationService: BusinessNavLocationService,
    private productLogoBankService: ProductLogoBankService,
    private modalService: ModalService
  ) {
    this.curServiceProviderId = this.configService.serviceProviderId$;
    this.formattedPath$ = combineLatest([this.configService.accountGroupId$, this.configService.groupPath$]).pipe(
        map(([accountGroupId, groupPath]: [string, string]) => {
          if (!this.path) {
            return this.path;
          }
          // Encode the brandPath if it has a pipe
          if (!!groupPath && groupPath.indexOf('|') > -1) {
            groupPath = encodeURIComponent(groupPath);
          }
          return this.path.replace('<accountGroupId>', accountGroupId).replace('<brandPath>', groupPath);
        })
    );
    this.formattedUrl = combineLatest([this.configService.accountGroupId$, this.configService.groupPath$]).pipe(
        map(([accountGroupId, groupPath]: [string, string]) => {
          if (!this.url) {
            return this.url;
          }
          // Encode the brandPath if it has a pipe
          if (!!groupPath && groupPath.indexOf('|') > -1) {
            groupPath = encodeURIComponent(groupPath);
          }
          return this.url.replace('<accountGroupId>', accountGroupId).replace('<brandPath>', groupPath);
        })
    );

    this.isActive$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => event.url),
        startWith(this.router.url),
        map((curPath: string) => decodeURI(`/${this.router.parseUrl(curPath).root.children.primary.toString()}`)),
        withLatestFrom(this.formattedPath$),
        map(([curPath, path]: [string, string]) => curPath === path),
        distinctUntilChanged(),
        shareReplay(1)
    );
  }

  ngOnInit(): void {
    if (!this.serviceProviderId) {
      this.curServiceProviderId.pipe(take(1)).subscribe(
          (serviceProviderId: string) => {
            this.serviceProviderId = serviceProviderId;
          }
      );
    }
    this.text$ = this.t.stream(this.translationId, this.label);
    this.isPinned$ = this.pinService.pins$.pipe(
        map((pins: PinnedItem[]) => (!pins) ? false : !!pins.find((pin: PinnedItem) => pin.navigationId === this.navigationId)),
        publishReplay(1),
        refCount()
    );

    if (!!this.logoUrl && !!this.serviceProviderId) {
      this.productLogoBankService.storeLogo(this.serviceProviderId, this.logoUrl);
    }
  }

  public togglePin(event: Event): void {
    if (!this.pinnable) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isPinned$.pipe(
        take(1),
    ).subscribe((isPinned: boolean) => {
      if (isPinned) {
        this.pinService.removePin(this.navigationId);
      } else {
        this.pinService.addPin(this.navigationId);
      }
    });
  }

  public calcIndentation(indentation: number): number {
    if (!indentation) {
      return 0;
    }
    if (this.showIcon) {
      return (indentation - 1) * 24;
    }
    return indentation * 24;
  }

  switchPage(path$: Observable<string>, page: boolean, dimensions: {} = null, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.userRequired) {
      this.modalService.open(UserRequiredModalComponent);
    } else {
      path$.pipe(take(1)).subscribe(path => {
        if (this.ooProduct) {
          window.location.href = path;
        } else {
          if (page) {
            this.router.navigateByUrl(path);
          } else if (this.productNavigation) {
            window.open(path, '_blank', 'noopener');
          } else {
            window.location.href = path;
          }
        }
      });
    }
  }
}
